import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Lock } from "lucide-react";

const MAP = {
  Verified: "Verified",
  Demonstrated: "Demonstrated",
  "In development": "In development",
  "In active development": "In development",
  Pilot: "Pilot",
  "Research framework": "Research framework",
  Historical: "Historical implementation",
  "Historical implementation": "Historical implementation",
  "Historical capability": "Historical implementation",
  "Available in network": "In development",
  "Public product": "Public product",
  "Patent record": "Patent record",
  Proposed: "Research framework",
  "Proposed research area": "Research framework",
  Illustrative: "Illustrative",
};

const STYLES = {
  Verified: { bg: "#E6F4F0", text: "#2F7D68", border: "#B2DFC2" },
  Demonstrated: { bg: "#E6F4F0", text: "#2F7D68", border: "#B2DFC2" },
  "In development": { bg: "#FEF7EC", text: "#B67620", border: "#F8E0B6" },
  Pilot: { bg: "#EBF3FB", text: "#2867B2", border: "#BDD8F2" },
  "Research framework": { bg: "#F3EEFB", text: "#6D4AAE", border: "#DDD1F1" },
  "Historical implementation": { bg: "#F1F3F5", text: "#69737E", border: "#D9E1E6" },
  "Public product": { bg: "#EAF6F8", text: "#227B8C", border: "#BDE4EB" },
  "Patent record": { bg: "#F4F0E6", text: "#8A6D2F", border: "#E4D8B8" },
  Illustrative: { bg: "#F8F5ED", text: "#10253F", border: "#D9E1E6" },
};

const DESC = {
  Verified: "Approved source or documented evidence.",
  Demonstrated: "A working implementation or documented test exists.",
  "In development": "Active technical work; not production-ready.",
  Pilot: "A scoped implementation is being evaluated.",
  "Research framework": "A technical or economic method to guide validation; not a performance result.",
  "Historical implementation": "Prior working development; current availability must be confirmed.",
  "Public product": "An externally visible product exists; this does not validate every performance statement.",
  "Patent record": "A verified filed or granted record; do not infer product performance from patent status.",
  Illustrative: "An example structure, not a DBRF result.",
};

export function EvidenceBadge({ status, raw, className = "" }) {
  const label = MAP[status] || status || "Illustrative";
  const s = STYLES[label] || STYLES.Illustrative;
  const testid = `evidence-badge-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-mono font-medium ${className}`}
            style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.text }} />
            {raw && raw !== label ? raw : label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{DESC[label] || "Evidence state."}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ConfidentialBadge({ className = "" }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="confidential-oem-badge"
            className={`inline-flex items-center gap-1.5 rounded-full border border-[var(--navy)]/20 bg-[var(--navy)] px-2.5 py-1 font-mono text-xs font-medium text-[var(--ivory)] ${className}`}
          >
            <Lock size={11} /> Confidential OEM
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Delivered under an OEM / confidential development structure. The contracting organisation is
          not identified; IP remains with the applicable rights holder.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
